/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description constants
 */

import _ from 'lodash';

const APP_FEATURES: Record<App.Feature, App.Feature> = {
	default: 'default',
	codeexplorer: 'codeexplorer',
};

const ENTITY = {
	eligibility: {
		key: 'eligibility' as Menu.Entity,
		path: 'eligibility' as Menu.Entity,
		title: 'Eligibility',
	},
	claims: {
		key: 'claims' as Menu.Entity,
		path: 'claims' as Menu.Entity,
		title: 'Claims',
	},
	payments: {
		key: 'payments' as Menu.Entity,
		path: 'payments' as Menu.Entity,
		title: 'Payments',
	},
	accounts: {
		key: 'accounts' as Menu.Entity,
		path: 'accounts' as Menu.Entity,
		title: 'Accounts',
	},
	patients: {
		key: 'patients' as Menu.Entity,
		path: 'patients' as Menu.Entity,
		title: 'Patients',
	},
	codeExplorer: {
		key: 'code-explorer' as Menu.Entity,
		path: 'code-explorer' as Menu.Entity,
		title: 'Code Explorer',
	},
	settings: {
		key: 'settings' as Menu.Entity,
		path: 'settings' as Menu.Entity,
		title: 'Settings',
	},
	help: {
		key: 'help' as Menu.Entity,
		path: 'help' as Menu.Entity,
		title: 'Help',
	},
	analytics: {
		key: 'analytics' as Menu.Entity,
		path: 'analytics' as Menu.Entity,
		title: 'Analytics',
	},
};

const ONBOARDING_ENTITIES: Menu.Entity[] = [ENTITY.eligibility.key, ENTITY.claims.key, ENTITY.payments.key];

const SETTINGS_SECTIONS = {
	Organization: 'organization',
	DataManagement: 'data-management',
	Integration: 'integration',
};

/**
 * Page keys for the app
 */
const PAGE_KEYS = {
	eligibility: {
		section: ENTITY.eligibility.key,
		// Pages
		responses: `${ENTITY.eligibility.key}-responses`,
		newRequest: `${ENTITY.eligibility.key}-new-request`,
		editRequest: `${ENTITY.eligibility.key}-edit-request`,
		viewResponse: `${ENTITY.eligibility.key}-view-response`,
		batch: {
			add: `${ENTITY.eligibility.key}-add-batch`,
			edit: `${ENTITY.eligibility.key}-edit-batch`,
			viewResponse: `${ENTITY.eligibility.key}-view-batch-response`,
		},
	},
	claims: {
		section: ENTITY.claims.key,
		// Pages
		responses: `${ENTITY.claims.key}-responses`,
		allResponses: `${ENTITY.claims.key}-all-responses`,
		archivedResponses: `${ENTITY.claims.key}-archived-responses`,
		draftClaims: `${ENTITY.claims.key}-draft-claims`,
		newRequest: `${ENTITY.claims.key}-new-request`,
		viewResponse: `${ENTITY.claims.key}-view-response`,
		editRequest: `${ENTITY.claims.key}-edit-request`,
		resubmit: `${ENTITY.claims.key}-resubmit`,
		reviewSecondaryClaim: `${ENTITY.claims.key}-review-secondary-claim`,
		filePrimaryClaim: `${ENTITY.claims.key}-file-primary-claim`,
		eligibilityResponse: `${ENTITY.claims.key}-eligibility`,
		newEligibilityRequest: `${ENTITY.claims.key}-new-eligibility`,
		editEligibilityRequest: `${ENTITY.claims.key}-eligibility-request`,
		// worklist
		worklist: {
			listView: `${ENTITY.claims.key}-worklist`,
			add: `${ENTITY.claims.key}-worklist-add`,
			addManual: `${ENTITY.claims.key}-worklist-add-manual`,
			edit: `${ENTITY.claims.key}-worklist-edit`,
			viewResponse: `${ENTITY.claims.key}-worklist-view-response`,
		},
	},
	payments: {
		section: ENTITY.payments.key,
		// Pages
		allPayments: `${ENTITY.payments.key}-all-payments`,
		recordPayment: `${ENTITY.payments.key}-record-payment`,
		paymentAllocation: `${ENTITY.payments.key}-payment-allocation`,
		// era
		era: {
			listView: `${ENTITY.claims.key}-era`,
			viewResponse: `${ENTITY.claims.key}-era-view-response`,
		},
	},
	accounts: {
		section: ENTITY.accounts.key,
		allAccounts: `${ENTITY.accounts.key}-all`,
		accountDetails: `${ENTITY.accounts.key}-account-details`,
		guarantor: `${ENTITY.accounts.key}-guarantor`,
	},
	patients: {
		section: ENTITY.patients.key,
		allPatients: `${ENTITY.patients.key}-all`,
		PatientDetails: `${ENTITY.patients.key}-patient-and-insurance`,
	},
	codeExplorer: {
		section: ENTITY.codeExplorer.key,
		// Pages
		allCodes: `${ENTITY.codeExplorer.key}-all-codes`,
		searchResults: `${ENTITY.codeExplorer.key}-search-results`,
		codeSetSearchResults: `${ENTITY.codeExplorer.key}-code-set-search-results`,
		codeDetails: `${ENTITY.codeExplorer.key}-code-details`,
		favoriteCodes: `${ENTITY.codeExplorer.key}-favorite-codes`,
		codeList: `${ENTITY.codeExplorer.key}-code-list`,
	},
	analytics: {
		section: ENTITY.analytics.key,
		// Pages
		dashboard: `${ENTITY.analytics.key}-dashboard`,
	},
	help: {
		landing: ENTITY.help.key,
	},
	settings: {
		organization: {
			section: SETTINGS_SECTIONS.Organization,
			// Pages
			general: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-general`,
			subscriptions: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-subscriptions`,
			auditLogs: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-audit-logs`,
			users: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-users`,
				add: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-add-user`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Organization}-edit-user`,
			},
		},
		dataManagement: {
			section: SETTINGS_SECTIONS.DataManagement,
			// Pages
			providers: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-providers`,
				add: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-add-provider`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-provider`,
			},
			locations: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-locations`,
				add: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-add-location`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-location`,
			},
			payers: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-payers`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-payer`,
			},
			worklists: {
				config: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-worklists`,
			},
			claims: {
				config: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-claims`,
			},
			customAttributes: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-custom-attributes`,
				add: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-add-custom-attribute`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-custom-attribute`,
			},
		},
		integration: {
			section: SETTINGS_SECTIONS.Integration,
			// Pages
			payment: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Integration}-payment`,
			clearingHouse: {
				listView: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Integration}-clearing-house`,
				add: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Integration}-add-clearing-house`,
				edit: `${ENTITY.settings.key}-${SETTINGS_SECTIONS.Integration}-edit-clearing-house`,
			},
		},
	},
};

// TODO - define menu config attribute if this feature is retained
const ONBOARDING_SETTINGS_PAGES: string[] = [
	..._.values(PAGE_KEYS.settings.dataManagement.providers),
	..._.values(PAGE_KEYS.settings.dataManagement.locations),
	..._.values(PAGE_KEYS.settings.dataManagement.payers),
	PAGE_KEYS.settings.integration.payment,
];

// TODO - add icons in next release
const ICON_MAP = {
	default: '' as Menu.IconType,
	eligibility: {
		responses: 'PeopleSafe' as Menu.IconType,
		newRequest: 'PeopleSafe' as Menu.IconType,
		editRequest: 'PeopleSafe' as Menu.IconType,
		viewResponse: 'PeopleSafe' as Menu.IconType,
		batch: {
			add: 'PeopleSafe' as Menu.IconType,
			edit: 'PeopleSafe' as Menu.IconType,
			viewResponse: 'PeopleSafe' as Menu.IconType,
		},
	},
	claims: {
		responses: 'Claims' as Menu.IconType,
		allResponses: '' as Menu.IconType,
		archivedResponses: '' as Menu.IconType,
		draftClaims: '' as Menu.IconType,
		newRequest: 'Claims' as Menu.IconType,
		viewResponse: 'Claims' as Menu.IconType,
		editRequest: 'Claims' as Menu.IconType,
		resubmit: 'Claims' as Menu.IconType,
		reviewSecondaryClaim: 'Claims' as Menu.IconType,
		filePrimaryClaim: 'Claims' as Menu.IconType,
		worklist: {
			listView: 'ListBottom' as Menu.IconType,
			add: 'ListBottom' as Menu.IconType,
			addManual: 'ListBottom' as Menu.IconType,
			edit: 'ListBottom' as Menu.IconType,
			viewResponse: 'ListBottom' as Menu.IconType,
		},
	},
	payments: {
		allPayments: 'PatientPayments' as Menu.IconType,
		recordPayment: 'PatientPayments' as Menu.IconType,
		paymentAllocation: 'PatientPayments' as Menu.IconType,
		era: {
			listView: 'InsurancePayments' as Menu.IconType,
			viewResponse: 'InsurancePayments' as Menu.IconType,
		},
	},
	// todo designer to provide
	accounts: {
		allAccounts: 'Palace' as Menu.IconType,
		accountDetails: 'Palace' as Menu.IconType,
		guarantor: 'Palace' as Menu.IconType,
	},
	patients: {
		allPatients: 'User' as Menu.IconType,
		PatientDetails: 'User' as Menu.IconType,
	},
	codeExplorer: {
		allCodes: 'AllCodes' as Menu.IconType,
		searchResults: 'AllCodes' as Menu.IconType,
		codeDetails: 'AllCodes' as Menu.IconType,
		favoriteCodes: 'FavoriteCodes' as Menu.IconType,
		codeList: 'AllCodes' as Menu.IconType,
	},
	settings: {
		organization: {
			general: '' as Menu.IconType,
			subscriptions: '' as Menu.IconType,
			auditLogs: '' as Menu.IconType,
			users: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
		},
		dataManagement: {
			providers: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
			locations: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
			payers: {
				listView: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
			worklists: {
				config: '' as Menu.IconType,
			},
			claims: {
				config: '' as Menu.IconType,
			},
			customAttributes: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
		},
		integration: {
			payment: '' as Menu.IconType,
			clearingHouse: {
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
				listView: '' as Menu.IconType,
			},
		},
	},
	analytics: {
		dashboard: 'ChartHistogramTwo' as Menu.IconType,
	},
	help: {
		landing: '' as Menu.IconType,
	},
};

/**
 * Routes for the app
 * Dynamic required param like /:id in the url path must be only added in extra routes
 * Dynamic optional param like /:tab? in the url path can be added in routes (pages)
 */
const ROUTES = {
	home: {
		landing: '/',
		match: '*',
	},
	logout: {
		landing: '/logout',
		redirect: ':origin?/logout',
	},
	eligibility: {
		landing: `/${ENTITY.eligibility.path}`,
		responses: `/${ENTITY.eligibility.path}/responses/:tab?`,
		newRequest: `/${ENTITY.eligibility.path}/new-request`,
		editRequest: `/${ENTITY.eligibility.path}/request/:id`,
		viewResponse: `/${ENTITY.eligibility.path}/response/:id`,
		batch: {
			add: `/${ENTITY.eligibility.path}/upload-batch/:id?`,
			edit: `/${ENTITY.eligibility.path}/batch/:id`,
			viewResponse: `/${ENTITY.eligibility.path}/batch/response/:id`,
		},
	},
	claims: {
		landing: `/${ENTITY.claims.path}`,
		responses: `/${ENTITY.claims.path}/responses`,
		allResponses: `/${ENTITY.claims.path}/responses/all/:tab?`,
		archivedResponses: `/${ENTITY.claims.path}/responses/archived/:tab?`,
		draftClaims: `/${ENTITY.claims.path}/responses/draft`,
		viewResponse: `/${ENTITY.claims.path}/response/:id`,
		newRequest: `/${ENTITY.claims.path}/new-request`,
		editRequest: `/${ENTITY.claims.path}/request/:id`,
		resubmit: `/${ENTITY.claims.path}/resubmit/:id`,
		reviewSecondaryClaim: `/${ENTITY.claims.path}/review-secondary-claim/:id`,
		filePrimaryClaim: `/${ENTITY.claims.path}/file-primary-claim/:type`,
		eligibilityResponse: `/${ENTITY.claims.path}/eligibility/:id`,
		newEligibilityRequest: `/${ENTITY.claims.path}/new-eligibility`,
		editEligibilityRequest: `/${ENTITY.claims.path}/eligibility-request`,
		// worklist
		worklist: {
			listView: `/${ENTITY.claims.path}/worklists/:tab?`,
			add: `/${ENTITY.claims.path}/create-worklist`,
			addManual: `/${ENTITY.claims.path}/create-manual-worklist`,
			edit: `/${ENTITY.claims.path}/worklist/:id`,
			viewResponse: `/${ENTITY.claims.path}/worklist/response/:id/:tab?`,
		},
	},
	help: {
		landing: `/${ENTITY.help.path}`,
	},
	payments: {
		landing: `/${ENTITY.payments.path}`,
		allPayments: `/${ENTITY.payments.path}/all-payments/:tab?`,
		recordPayment: `/${ENTITY.payments.path}/create-record`,
		editPaymentDetails: `/${ENTITY.payments.path}/edit-record/:id`,
		paymentAllocation: `/${ENTITY.payments.path}/allocation/:id`,
		era: {
			listView: `/${ENTITY.payments.path}/insurance-payments/:tab?`,
			viewResponse: `/${ENTITY.payments.path}/insurance-payment/response/:id/:tab?`,
		},
	},
	accounts: {
		landing: `/${ENTITY.accounts.path}`,
		allAccounts: `/${ENTITY.accounts.path}/all`,
		accountDetails: `/${ENTITY.accounts.path}/details/:id/:tab?`,
		guarantor: `/${ENTITY.accounts.path}/guarantor/:id`,
	},
	patients: {
		landing: `/${ENTITY.patients.path}`,
		allPatients: `/${ENTITY.patients.path}/all`,
		PatientDetails: `/${ENTITY.patients.path}/:id/:tab?`,
	},
	codeExplorer: {
		landing: `/${ENTITY.codeExplorer.path}`,
		allCodes: `/${ENTITY.codeExplorer.path}/all-codes`,
		searchResults: `/${ENTITY.codeExplorer.path}/search/:searchTerm?`,
		codeSetSearchResults: `/${ENTITY.codeExplorer.path}/search/:codeSet/:searchTerm?`,
		codeDetails: `/${ENTITY.codeExplorer.path}/code/:codeSet/:code/:tab?`,
		favoriteCodes: `/${ENTITY.codeExplorer.path}/favorites/:searchTerm?`,
		codeList: `/${ENTITY.codeExplorer.path}/code-list/:codeSet/:searchTerm?`,
	},
	analytics: {
		landing: `/${ENTITY.analytics.path}`,
		dashboard: `/${ENTITY.analytics.path}/dashboard`,
	},
	settings: {
		landing: `/${ENTITY.settings.path}`,
		match: `/${ENTITY.settings.path}/*`,
		organization: {
			landing: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}`,
			general: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/general`,
			subscriptions: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/subscriptions/:tab?`,
			auditLogs: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/audit-logs`,
			users: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/users`,
				add: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/add-user`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Organization}/user/:id`,
			},
		},
		dataManagement: {
			landing: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}`,
			providers: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/providers`,
				add: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/add-provider`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/provider/:id`,
			},
			locations: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/locations`,
				add: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/add-location`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/location/:id`,
			},
			payers: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/payers`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/payer/:id/:tab?`,
			},
			worklists: {
				config: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/worklists`,
			},
			claims: {
				config: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/claims`,
			},
			customAttributes: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/custom-attributes`,
				add: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/add-custom-attribute`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.DataManagement}/custom-attribute/:id`,
			},
		},
		integration: {
			landing: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Integration}`,
			payment: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Integration}/payment`,
			clearingHouse: {
				listView: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Integration}/clearinghouses`,
				add: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Integration}/add-clearinghouse`,
				edit: `/${ENTITY.settings.path}/${SETTINGS_SECTIONS.Integration}/clearinghouse/:id`,
			},
		},
	},
};

const PAGE_TITLE = {
	eligibility: {
		responses: 'Responses',
		newRequest: 'New Request',
		viewResponse: 'Eligibility Response',
		editRequest: 'Edit Eligibility Request',
		batch: {
			add: 'New Batch',
			edit: 'Edit Batch',
			viewResponse: 'Batch Response',
		},
	},
	claims: {
		responses: 'Claims',
		newRequest: 'Check New Status',
		allResponses: 'Ongoing',
		archivedResponses: 'Archived',
		draftClaims: 'Drafts',
		viewResponse: 'Claim Status Response',
		editRequest: 'Edit Claim Status',
		resubmit: 'Resubmit Claim Status',
		reviewSecondaryClaim: 'Review Secondary Claim',
		filePrimaryClaim: 'File Primary Claim',
		eligibility: 'Eligibility Detail',
		newEligibility: 'New Eligibility',
		eligibilityRequest: 'Edit Eligibility Request',
		worklist: {
			listView: 'Worklists',
			add: 'Create Automated Worklist',
			addManual: 'Create Manual Worklist',
			edit: 'Edit Worklist',
			viewResponse: 'Worklist Response',
		},
	},
	payments: {
		allPayments: 'Patient',
		recordPayment: 'Record Payment',
		paymentAllocation: 'Payment Allocation',
		era: {
			listView: 'Insurance',
			viewResponse: 'ERA Response',
		},
	},
	accounts: {
		allAccounts: 'Accounts',
		accountDetails: 'Account Details',
		guarantor: 'Guarantor',
	},
	patients: {
		allPatients: 'Patients',
		PatientDetails: 'Patient and Insurance',
	},
	codeExplorer: {
		allCodes: 'All Codes',
		searchResults: 'Search Results',
		codeDetails: 'Code Details',
		favoriteCodes: 'Favorites',
		codeList: 'ICD-10-PCS Code Lookup', // TODO : add dynamic title later
	},

	analytics: {
		dashboard: 'Dashboard',
	},

	help: {
		landing: 'Help/FAQs',
	},
	settings: {
		organization: {
			general: 'General',
			subscriptions: 'Subscription',
			auditLogs: 'Audit Log',
			users: {
				listView: 'Users',
				add: 'Add User',
				edit: 'Edit User',
			},
		},
		dataManagement: {
			providers: {
				listView: 'Providers',
				add: 'Add Provider',
				edit: 'Edit Provider',
			},
			locations: {
				listView: 'Locations',
				add: 'Add Location',
				edit: 'Edit Location',
			},
			payers: {
				listView: 'Payers',
				edit: 'Edit Payer',
			},
			worklists: {
				config: 'Worklists',
			},
			claims: {
				config: 'Claims',
			},
			customAttributes: {
				listView: 'Custom Fields',
				add: 'Add Custom Field',
				edit: 'Edit Custom Field',
			},
		},
		integration: {
			clearingHouse: {
				listView: 'Clearinghouse',
				add: 'Add New Clearinghouse',
				edit: 'Edit Clearinghouse',
			},
		},
	},
};

const getSearchMeta = (isBillingCustomer = false): AnyRecord => ({
	default: {
		title: '',
		description: '',
		disable: false,
	} as Menu.SearchMeta,
	// Analytics
	analytics: {
		dashboard: {
			title: 'Analytics Dashboard',
			description: 'Access key metrics and insights to monitor overall A/R health and team performance',
			disable: false,
		} as Menu.SearchMeta,
	},
	// Claims
	claims: {
		newRequest: {
			title: 'New Claim Status Check',
			description: 'Check the status of a submitted insurance claim',
			disable: isBillingCustomer,
		} as Menu.SearchMeta,
		responses: {
			title: 'Claim Response',
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		allResponses: {
			title: isBillingCustomer ? 'Ongoing Claims' : 'Claims',
			description: 'Monitor all claims with real-time status, payer, and balance insights',
			disable: false,
		} as Menu.SearchMeta,
		archivedResponses: {
			title: 'Archived Claims',
			description: 'Review historical claims that have been archived after completion of A/R activities',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		draftClaims: {
			title: 'Draft Claims',
			description: 'View and manage all draft claims',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		eligibility: {
			title: 'Claim Eligibility',
			description: 'Eligibility Data for Patient',
			disable: true,
		} as Menu.SearchMeta,
		newEligibility: {
			title: 'New Eligibility',
			description: 'New Eligibility',
			disable: true,
		} as Menu.SearchMeta,
		eligibilityRequest: {
			title: 'Edit Eligibility Request',
			description: 'Eligibility Request',
			disable: true,
		} as Menu.SearchMeta,

		viewResponse: {
			title: PAGE_TITLE.claims.viewResponse,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		editRequest: {
			title: PAGE_TITLE.claims.editRequest,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		resubmit: {
			title: PAGE_TITLE.claims.resubmit,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		reviewSecondaryClaim: {
			title: PAGE_TITLE.claims.reviewSecondaryClaim,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		filePrimaryClaim: {
			title: PAGE_TITLE.claims.filePrimaryClaim,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		// worklist
		worklist: {
			listView: {
				title: PAGE_TITLE.claims.worklist.listView,
				description: isBillingCustomer ? 'View and manage all claim worklists' : 'View and manage all worklists',
				disable: !isBillingCustomer,
			} as Menu.SearchMeta,
			add: {
				title: 'Create New Automated Worklist',
				description: 'Set up dynamic worklists that automatically track claims based on predefined rules and payer updates',
				disable: !isBillingCustomer,
			} as Menu.SearchMeta,
			addManual: {
				title: 'Create New Manual Worklist',
				description: 'Build custom worklists by manually selecting and grouping claims',
				disable: !isBillingCustomer,
			} as Menu.SearchMeta,
			edit: {
				title: PAGE_TITLE.claims.worklist.edit,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
			viewResponse: {
				title: PAGE_TITLE.claims.worklist.viewResponse,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
		},
	},
	// accounts
	accounts: {
		allAccounts: {
			title: 'Accounts & Guarantors',
			description: 'Review and manage all patient accounts, open claims, balances, and guarantor details',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		accountDetails: {
			title: PAGE_TITLE.accounts.accountDetails,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		guarantor: {
			title: PAGE_TITLE.accounts.guarantor,
			description: 'View guarantors against their respective accounts and patients',
			disable: true,
		} as Menu.SearchMeta,
	},
	// todo check from here
	// payments
	payments: {
		allPayments: {
			title: 'Patient Payments',
			description: 'View and manage all patient/guarantor payments, including allocated and unallocated amounts',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		recordPayment: {
			title: 'Record Payment',
			description: 'Record payments received from patients/guarantors toward their outstanding balances',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		paymentAllocation: {
			title: PAGE_TITLE.payments.paymentAllocation,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		era: {
			listView: {
				title: 'Insurance Payments (ERA)',
				description: 'View and reconcile Insurance Payment Report (ERA)',
				disable: !isBillingCustomer,
			} as Menu.SearchMeta,
			viewResponse: {
				title: PAGE_TITLE.payments.era.viewResponse,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
		},
	},
	// patients
	patients: {
		allPatients: {
			title: PAGE_TITLE.patients.allPatients,
			description: 'Browse patients to view their account, and payment summaries.',
			disable: !isBillingCustomer,
		} as Menu.SearchMeta,
		PatientDetails: {
			title: PAGE_TITLE.patients.PatientDetails,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
	},
	// Eligibility
	eligibility: {
		responses: {
			title: 'Eligibility Responses',
			description: 'View detailed responses of submitted eligibility requests',
			disable: isBillingCustomer,
		} as Menu.SearchMeta,
		newRequest: {
			title: 'New Eligibility Request',
			description: 'Submit a new eligibility check for a single patient',
			disable: isBillingCustomer,
		} as Menu.SearchMeta,
		viewResponse: {
			title: PAGE_TITLE.eligibility.viewResponse,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		editRequest: {
			title: PAGE_TITLE.eligibility.editRequest,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		// Batch
		batch: {
			add: {
				title: 'New Batch Eligibility Request',
				description: 'Submit eligibility checks for multiple patients at once by uploading a batch file',
				disable: isBillingCustomer,
			} as Menu.SearchMeta,
			edit: {
				title: PAGE_TITLE.eligibility.batch.edit,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
			viewResponse: {
				title: PAGE_TITLE.eligibility.batch.viewResponse,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
		},
	},

	// Code Explorer
	codeExplorer: {
		allCodes: {
			title: 'Code Explorer - All Codes',
			description: 'Search across all ICD-10, CPT, HCPCS, MS-DRG, and HIPPS codes',
			disable: false,
		} as Menu.SearchMeta,
		searchResults: {
			title: PAGE_TITLE.codeExplorer.searchResults,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		codeDetails: {
			title: PAGE_TITLE.codeExplorer.codeDetails,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		favoriteCodes: {
			title: 'Code Explorer - Favorite Codes',
			description: "View the codes you've marked as 'favorites'",
			disable: false,
		} as Menu.SearchMeta,
		codeList: {
			title: PAGE_TITLE.codeExplorer.codeList,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
	},
	// Help
	help: {
		landing: {
			title: PAGE_TITLE.help.landing,
			description: 'Help & FAQs, Payment questions, Support, Contact us',
			disable: false,
		} as Menu.SearchMeta,
	},
	// Settings
	settings: {
		organization: {
			general: {
				title: 'Organization Settings',
				description: 'Update and manage organization-wide settings',
				disable: false,
			} as Menu.SearchMeta,
			subscriptions: {
				title: 'Subscription Details',
				description: 'View your subscription plan and billing information',
				disable: false,
			} as Menu.SearchMeta,
			auditLogs: {
				title: 'Audit Log',
				description: 'Track and review system activity and changes',
				disable: false,
			} as Menu.SearchMeta,
			// Users
			users: {
				listView: {
					title: 'User Management',
					description: 'View and manage user accounts and roles',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.organization.users.add,
					description: 'Add a new user to the organization and assign roles',
					disable: isBillingCustomer,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.organization.users.edit,
					description: '',
					disable: true,
				} as Menu.SearchMeta,
			},
		},
		// Data Management
		dataManagement: {
			providers: {
				listView: {
					title: 'Provider Settings',
					description: 'View and manage provider information',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.dataManagement.providers.add,
					description: 'Add a new provider to your organization',
					disable: isBillingCustomer,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.providers.edit,
					description: 'Manage Provider status, Identity Verification, Enrollment Specific Information',
					disable: true,
				} as Menu.SearchMeta,
			},
			locations: {
				listView: {
					title: 'Location Settings',
					description: 'View and manage organization locations',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.dataManagement.locations.add,
					description: 'Add a new location to your organization',
					disable: isBillingCustomer,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.locations.edit,
					description: 'Manage Location, Status, Address, Contact Info',
					disable: true,
				} as Menu.SearchMeta,
			},
			payers: {
				listView: {
					title: 'Payer Settings',
					description: 'View and manage insurance payers',
					disable: false,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.payers.edit,
					description: 'Manage Enrollment, Contact info and Maintenance info',
					disable: true,
				} as Menu.SearchMeta,
			},
			worklists: {
				config: {
					title: 'Worklist Settings',
					description: 'Manage Claims Status Check frequency for worklists',
					disable: !isBillingCustomer,
				} as Menu.SearchMeta,
			},
			claims: {
				config: {
					title: 'Claim Settings',
					description: 'View and manage claim-related settings',
					disable: !isBillingCustomer,
				} as Menu.SearchMeta,
			},
			customAttributes: {
				listView: {
					title: 'Custom Fields',
					description: 'View and manage custom fields for entities',
					disable: !isBillingCustomer,
				} as Menu.SearchMeta,
				add: {
					title: 'Add Custom Field',
					description: 'Add a new custom attribute',
					disable: true,
				} as Menu.SearchMeta,
				edit: {
					title: 'Edit Custom Field',
					description: 'Edit a custom attribute',
					disable: true,
				} as Menu.SearchMeta,
			},
		},
		integration: {
			payment: {
				title: 'Payment Gateway Settings',
				description: 'Manage payment gateway settings',
				disable: true,
			} as Menu.SearchMeta,
			clearingHouse: {
				listView: {
					title: 'Clearinghouse Settings',
					description: 'View and manage clearinghouse settings',
					disable: !isBillingCustomer,
				} as Menu.SearchMeta,
				add: {
					title: 'Add Clearing House',
					description: 'Add a new clearing house',
					disable: true,
				} as Menu.SearchMeta,
				edit: {
					title: 'Edit Clearing House',
					description: 'Edit a clearing house',
					disable: true,
				} as Menu.SearchMeta,
			},
		},
	},
});

export { APP_FEATURES, ENTITY, ROUTES, PAGE_KEYS, ICON_MAP, getSearchMeta, PAGE_TITLE, ONBOARDING_ENTITIES, ONBOARDING_SETTINGS_PAGES };
